import React, { useState } from 'react';
import { Server, Code, Cloud, Shield, Lightbulb, Headphones, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { mockServices } from '../mockData';
import { Link } from 'react-router-dom';

const iconMap = {
  Server, Code, Cloud, Shield, Lightbulb, Headphones
};

const Services = () => {
  const [selectedService, setSelectedService] = useState(null);

  return (
    <div className="min-h-screen bg-slate-950 pt-24 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16 space-y-4 animate-in fade-in duration-700">
          <h1 className="text-5xl md:text-6xl font-bold text-white">
            Nos <span className="bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">Services</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Des solutions technologiques complètes conçues pour transformer votre entreprise et accélérer votre croissance numérique
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {mockServices.map((service, index) => {
            const Icon = iconMap[service.icon];
            return (
              <Card 
                key={service.id} 
                className="bg-slate-900 border-slate-800 hover:border-teal-500/50 transition-all duration-300 group cursor-pointer hover:shadow-xl hover:shadow-teal-500/10"
                onClick={() => setSelectedService(service)}
              >
                <CardHeader>
                  <div className="w-16 h-16 bg-gradient-to-br from-teal-500/20 to-cyan-500/20 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <Icon className="text-teal-400" size={32} />
                  </div>
                  <CardTitle className="text-white text-xl group-hover:text-teal-400 transition-colors">
                    {service.title}
                  </CardTitle>
                  <CardDescription className="text-gray-400 text-base">
                    {service.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button 
                    variant="ghost" 
                    className="text-teal-400 hover:text-teal-300 hover:bg-teal-500/10 w-full group/btn"
                  >
                    En savoir plus
                    <ArrowRight className="ml-2 group-hover/btn:translate-x-1 transition-transform" size={16} />
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Selected Service Detail */}
        {selectedService && (
          <Card className="bg-gradient-to-br from-slate-900 to-slate-800 border-teal-500/30 animate-in slide-in-from-bottom duration-500">
            <CardContent className="p-8 md:p-12">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div className="space-y-6">
                  <div className="inline-block">
                    {(() => {
                      const Icon = iconMap[selectedService.icon];
                      return (
                        <div className="w-20 h-20 bg-gradient-to-br from-teal-500/20 to-cyan-500/20 rounded-2xl flex items-center justify-center">
                          <Icon className="text-teal-400" size={40} />
                        </div>
                      );
                    })()}
                  </div>
                  <h2 className="text-3xl font-bold text-white">{selectedService.title}</h2>
                  <p className="text-gray-300 text-lg leading-relaxed">{selectedService.details}</p>
                  <div className="flex gap-4">
                    <Link to="/contact">
                      <Button className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white">
                        Demander un devis
                      </Button>
                    </Link>
                    <Button 
                      variant="outline" 
                      className="border-teal-500/50 text-teal-400 hover:bg-teal-500/10"
                      onClick={() => setSelectedService(null)}
                    >
                      Fermer
                    </Button>
                  </div>
                </div>
                <div className="relative">
                  <img 
                    src="https://images.unsplash.com/photo-1606211105533-0439bfecce21?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjB0ZWNobm9sb2d5fGVufDB8fHx8MTc2OTI5MzM4OXww&ixlib=rb-4.1.0&q=85"
                    alt={selectedService.title}
                    className="rounded-xl shadow-2xl"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Process Section */}
        <section className="mt-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Notre <span className="bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">Processus</span>
            </h2>
            <p className="text-gray-400 text-lg">Une approche structurée pour garantir votre réussite</p>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {[
              { step: '01', title: 'Analyse', desc: 'Comprendre vos besoins et objectifs' },
              { step: '02', title: 'Conception', desc: 'Créer une solution sur mesure' },
              { step: '03', title: 'Développement', desc: 'Implémenter avec excellence' },
              { step: '04', title: 'Support', desc: 'Accompagnement continu' }
            ].map((item, index) => (
              <Card key={index} className="bg-slate-900 border-slate-800 hover:border-teal-500/50 transition-all duration-300 group">
                <CardContent className="p-6 text-center">
                  <div className="text-5xl font-bold bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent mb-4 group-hover:scale-110 transition-transform">
                    {item.step}
                  </div>
                  <h3 className="text-white font-semibold text-lg mb-2">{item.title}</h3>
                  <p className="text-gray-400 text-sm">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="mt-20">
          <Card className="bg-gradient-to-br from-teal-500/10 to-cyan-500/10 border-teal-500/20">
            <CardContent className="p-12 text-center space-y-6">
              <h2 className="text-3xl font-bold text-white">
                Prêt à démarrer votre projet ?
              </h2>
              <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                Contactez-nous pour une consultation gratuite et découvrez comment nos services peuvent transformer votre entreprise.
              </p>
              <Link to="/contact">
                <Button className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white text-lg px-8 py-6 group">
                  Contactez-nous maintenant
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </section>
      </div>
    </div>
  );
};

export default Services;